import { useEffect, useState } from "react";
import type * as Y from "yjs";
import {
  type DeviceOnSheet,
  type DomainMaps,
  listAllDeviceLayouts,
  listDevices,
  type SheetId,
} from "@electro-planner/domain";

export type DeviceIndexEntry = {
  deviceId: string;
  designation: string;
  symbolId?: string;
  // En device kan vara placerad på flera ark (cross-sheet referencing).
  // Första sheet som hittas ligger i `primarySheet`; övriga i `additionalSheets`.
  primarySheet?: SheetId;
  additionalSheets: SheetId[];
  // Position på primary sheet — används av fitView för att zooma in.
  x?: number;
  y?: number;
};

/**
 * Observerar devices + deviceLayout och returnerar en platt lista för "Gå till"-
 * kommandopaletten. Ordningen speglar devices-map:en (stabil per insert-ordning).
 */
export function useDeviceIndex(
  doc: Y.Doc,
  maps: DomainMaps,
): DeviceIndexEntry[] {
  const [entries, setEntries] = useState<DeviceIndexEntry[]>(() =>
    buildIndex(maps),
  );

  useEffect(() => {
    setEntries(buildIndex(maps));

    const handler = () => setEntries(buildIndex(maps));
    maps.devices.observeDeep(handler);
    maps.deviceLayout.observeDeep(handler);
    return () => {
      maps.devices.unobserveDeep(handler);
      maps.deviceLayout.unobserveDeep(handler);
    };
  }, [doc, maps]);

  return entries;
}

function buildIndex(maps: DomainMaps): DeviceIndexEntry[] {
  const layoutsByDevice = new Map<string, DeviceOnSheet[]>();
  for (const layout of listAllDeviceLayouts(maps)) {
    const list = layoutsByDevice.get(layout.deviceId);
    if (list) list.push(layout);
    else layoutsByDevice.set(layout.deviceId, [layout]);
  }

  const devices = listDevices(maps);
  return devices.map((d) => {
    const layouts = layoutsByDevice.get(d.id) ?? [];
    const primary = layouts[0];
    const additional = layouts.slice(1).map((l) => l.sheetId);
    const symbolId =
      typeof d.attributes["symbolId"] === "string"
        ? (d.attributes["symbolId"] as string)
        : undefined;
    return {
      deviceId: d.id,
      designation: d.designation,
      symbolId,
      primarySheet: primary?.sheetId,
      additionalSheets: additional,
      x: primary?.x,
      y: primary?.y,
    };
  });
}
